import { ReactNode } from 'react';
import { Flex, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/button';
import styles from './form-modal.module.scss';

type FormModalProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  isLoading?: boolean;
  submitDisabled?: boolean;
  width?: number;
  children: ReactNode;
};

/**
 * The settings CRUD modal: a heading with a line under it, the form, then Cancel and the
 * action in our own buttons. antd's head and footer are off, because they pad and colour
 * themselves apart from the mockup's single box.
 */
export function FormModal({
  open,
  title,
  subtitle,
  onCancel,
  onSubmit,
  submitLabel,
  isLoading = false,
  submitDisabled = false,
  width = 500,
  children,
}: FormModalProps) {
  const { t } = useTranslation('common');

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      title={null}
      width={width}
      className={styles.modal}
      // An antd Form instance passed in from outside has to be mounted before the first open.
      forceRender
    >
      <Flex vertical gap={20}>
        <div>
          <h3 className={styles.title}>{title}</h3>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {children}

        <Flex gap={12} justify="flex-end">
          <Button buttonVariant="secondary" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button
            buttonVariant="primary"
            onClick={onSubmit}
            loading={isLoading}
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
